import type { Dispatch, SetStateAction, useState } from 'react'
import Debug from 'debug'
import type { StateCreator } from 'zustand'
import type { DescMessage, MessageInitShape } from '@bufbuild/protobuf'
import { create, fromBinary, toBinary } from '@bufbuild/protobuf'

import { base64ToBytes, bytesToBase64 } from '@jotta/utils/base64'
import { cookies } from '@jotta/utils/cookie'

const debug = Debug('jotta:protocookie')

export type ProtoCookie<T extends DescMessage> = ReturnType<
  typeof useState<MessageInitShape<T>>
>
type Options<T extends DescMessage> = {
  name: string
  maxAgeSeconds?: number
  type: T
}

function encodeCookie<T extends DescMessage>(
  type: T,
  nextValue: MessageInitShape<T>,
) {
  return bytesToBase64(toBinary(type, create(type, nextValue)))
}

function decodeCookie<T extends DescMessage>({ name, type }: Options<T>) {
  const initialCookieValue = cookies.get(name)
  try {
    return initialCookieValue
      ? fromBinary(type, base64ToBytes(initialCookieValue))
      : undefined
  } catch (e) {
    reportError(e)
  }
}

export const persistProtoCookie = <T extends DescMessage>(
  options: Options<T>,
): StateCreator<ProtoCookie<T>> => {
  const { name, maxAgeSeconds, type } = options
  return (set): ProtoCookie<T> => {
    return [
      decodeCookie(options),
      setStateAction =>
        set(previousState => {
          const previousValue = previousState[0]
          const nextValue =
            typeof setStateAction === 'function'
              ? setStateAction(previousValue)
              : setStateAction
          const options = {
            maxAge: maxAgeSeconds,
            path: '/',
          }
          if (nextValue) {
            debug('updating', name)
            cookies.set(name, encodeCookie(type, nextValue), options)
          } else {
            debug('removing', name)
            cookies.remove(name, options)
          }
          return [nextValue, previousState[1]]
        }, true),
    ]
  }
}
